import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';

import type { ChatOption } from './types';

// Define proper types instead of using 'any'
type StateValue =
  | string
  | number
  | boolean
  | null
  | Record<string, unknown>
  | unknown[];
type ToolArguments = Record<
  string,
  string | number | boolean | null | Record<string, unknown> | unknown[]
>;

/**
 * Ordered activity log for the read-only Agent's "Agent activity" panel
 * (AID-362). One entry per SDK-derived frame, appended in stream order so the
 * timeline can interleave thinking and tool steps and nest a subagent's steps
 * by `parentToolUseId`. Distinct from the legacy flat `toolCalls`/`thinkingSteps`
 * metadata arrays — only `useAgentChat` populates this, and only the
 * `AgentActivity` component reads it. Keyed by the SDK's stable ids so per-node
 * open state survives streaming re-renders (AID-363).
 */
export type AgentActivityEvent =
  | {
      kind: 'thinking';
      /** Reasoning text; empty when `redacted`. */
      text: string;
      redacted?: boolean;
      parentToolUseId?: string | null;
    }
  | {
      kind: 'tool_start';
      toolUseId: string;
      name: string;
      input?: unknown;
      parentToolUseId?: string | null;
      subagentType?: string;
      /** Client receive-time ISO stamp — pairs with `tool_complete` for duration (AID-367). */
      startedAt: string;
    }
  | {
      kind: 'tool_complete';
      toolUseId: string;
      isError: boolean;
      result?: string;
      resultTruncated?: boolean;
      endedAt: string;
    }
  | {
      kind: 'tool_denied';
      toolUseId: string;
      name: string;
      reasonCode: string;
      parentToolUseId?: string | null;
    }
  | {
      /** Turn-level failure (AID-368) — safe code only, mapped to copy in the UI. */
      kind: 'turn_error';
      code: string;
    };

/** Lifecycle of a form-prefill proposal, from arrival to the user's decision. */
export type FormPrefillProposalStatus =
  | 'pending'
  | 'applied'
  | 'dismissed'
  | 'stale';

/**
 * A set of values the agent proposes writing into the form the user is viewing
 * (AID-379; spec `docs/ai-chat-form-prefill-spec.md` §4.3). Carried on the
 * assistant message that produced it, mirroring `activity` — so it survives
 * streaming re-renders and stays tied to its turn rather than sitting in a global
 * slot that a later turn would clobber.
 *
 * Nothing here is persisted: applying it is a local `setValue` into the live
 * form, and the user still saves through the normal form path.
 */
export interface FormPrefillProposal {
  proposalId: string;
  entityType: string;
  /**
   * The record the proposal was computed FOR — captured from the `pageContext`
   * this turn was sent with, not from the form on screen when it arrived
   * (AID-381).
   *
   * The distinction is the whole point: the descriptor the agent proposed
   * against was snapshotted at send time, so if the user navigated to a
   * different record mid-turn, the arriving proposal belongs to the form they
   * LEFT. Stamping it from the live store on arrival would re-target the
   * proposal at whatever is on screen now and let `ProposalCard` write one
   * action's values into another. `null` for create forms (and for a turn sent
   * with no active form at all).
   */
  recordId: string | null;
  /**
   * Whether the turn that produced this proposal actually carried a form
   * descriptor.
   *
   * Needed because `recordId: null` is ambiguous on its own — it means BOTH "a
   * create form, which has no id yet" and "no form was in context at all".
   * Without this marker a proposal from an untargeted turn would match any
   * create form of the same `entityType` and become applicable there.
   *
   * The ai-agent only emits a proposal when at least one field validated
   * against the descriptor, so an untargeted proposal cannot reach us today —
   * but that invariant lives in a different service. This keeps the browser's
   * staleness guard self-contained rather than resting on it.
   */
  hasActiveForm: boolean;
  fields: { key: string; label: string; value: unknown; reason?: string }[];
  status: FormPrefillProposalStatus;
}

/**
 * The proposal as it arrives on the wire — the gateway's `form_prefill_proposal`
 * event carries no `status`, because a freshly-arrived proposal is always
 * `pending`. `setProposal` stamps it, keeping that invariant in the store instead
 * of trusting each caller to invent the right lifecycle value.
 */
export type IncomingFormPrefillProposal = Omit<FormPrefillProposal, 'status'>;

export interface AgentMetadata {
  workflowId?: string;
  workflowType?: string;
  /** Ordered agent-activity frames — see {@link AgentActivityEvent}. */
  activity?: AgentActivityEvent[];
  /** The turn's form-prefill proposal — see {@link FormPrefillProposal}. */
  proposal?: FormPrefillProposal;
  thinkingSteps?: Array<{
    timestamp: string;
    agentName: string;
    thinking: string;
  }>;
  reasoningSteps?: Array<{
    timestamp: string;
    agentName: string;
    reasoningType: 'analysis' | 'decision' | 'planning' | 'evaluation';
    reasoningContent: string;
    inputAnalysis?: string;
    decisionFactors?: string[];
    confidenceLevel: 'high' | 'medium' | 'low';
    alternativesConsidered?: string[];
    nextSteps?: string[];
  }>;
  stateUpdates?: Array<{
    timestamp: string;
    stateName: string;
    oldValue: StateValue;
    newValue: StateValue;
    changedByAgent: string;
  }>;
  toolCalls?: Array<{
    toolName: string;
    /** Stable per-invocation id from the gateway; correlates start↔complete. */
    toolUseId?: string;
    toolArgs?: ToolArguments;
    startTime: string;
    endTime?: string;
    success?: boolean;
    executionTimeMs?: number;
    calledByAgent?: string;
    error?: string;
    /** Result peek for agent-mode tool calls (gateway `tool_call_complete`). */
    result?: unknown;
    /** True when the agent cut `result` to its transport cap (AID-292). */
    resultTruncated?: boolean;
  }>;
  responseId?: string;
  traceId?: string;
  spanId?: string;
  ddApp?: string;
  /**
   * Session id this message's feedback submits under (AID-236). Stamped when the
   * message is created (the agent surrogate at that moment) so feedback stays
   * bound to the message's ORIGINATING session — never recomputed later, which
   * would misattribute or hide it after a "New chat" resets the session. Absent
   * when not stamped, in which case it falls back to the store sessionId.
   */
  feedbackSessionId?: string;
  totalChunks?: number;
  processingTime?: string;
}

export type FeedbackType = 'thumbs_up' | 'thumbs_down';

export interface ChatMessage {
  id: string;
  content: string;
  timestamp: Date;
  isUser: boolean;
  isSystem?: boolean;
  metadata?: AgentMetadata;
  feedbackGiven?: FeedbackType;
}

export interface ChatState {
  isOpen: boolean;
  messages: ChatMessage[];
  options: ChatOption[];
  isLoading: boolean;
  isInitialising: boolean;
  sessionId: string | null;
  /**
   * Agent-mode feedback session surrogate (AID-236). Kept SEPARATE from the
   * platform `sessionId` so it can never be mistaken for a platform session — a
   * surrogate left in `sessionId` wedges the platform bootstrap on an
   * entitlement-loss flip to platform mode (#8443 review).
   */
  agentSessionId: string | null;
  sessionInitialized: boolean;
  thinkingSteps: string[];
  currentThinkingStep: string | null;
  streamingMessageId: string | null;
  isStreaming: boolean;
  setIsOpen: (open: boolean) => void;
  addMessage: (content: string, isUser: boolean, isSystem?: boolean) => void;
  removeSystemMessages: () => void;
  setOptions: (options: ChatOption[]) => void;
  clearOptions: () => void;
  setIsLoading: (loading: boolean) => void;
  setIsInitialising: (initialising: boolean) => void;
  clearMessages: () => void;
  setSessionId: (sessionId: string | null) => void;
  setAgentSessionId: (agentSessionId: string | null) => void;
  setSessionInitialized: (initialized: boolean) => void;
  initializeSession: () => void;
  addThinkingStep: (step: string) => void;
  setCurrentThinkingStep: (step: string | null) => void;
  clearThinkingSteps: () => void;
  startStreamingMessage: (messageId: string) => void;
  appendToStreamingMessage: (content: string) => void;
  finishStreamingMessage: (finalContent?: string) => void;
  updateStreamingMetadata: (metadata: Partial<AgentMetadata>) => void;
  addThinkingToMetadata: (
    agentName: string,
    thinking: string,
    timestamp: string
  ) => void;
  addToolCallToMetadata: (
    toolName: string,
    startTime: string,
    toolArgs?: ToolArguments,
    toolUseId?: string
  ) => void;
  completeToolCallInMetadata: (params: {
    toolName: string;
    endTime: string;
    success?: boolean;
    result?: unknown;
    resultTruncated?: boolean;
    toolUseId?: string;
  }) => void;
  /** Append one frame to the streaming agent message's activity log (AID-362). */
  pushAgentActivity: (event: AgentActivityEvent) => void;
  /**
   * Attach the agent's prefill proposal to a message as `pending` (AID-379).
   * Last-write-wins: a second proposal in the same turn replaces the first, so
   * the card always shows the agent's latest suggestion.
   */
  setProposal: (
    messageId: string,
    proposal: IncomingFormPrefillProposal
  ) => void;
  /** Advance a proposal's lifecycle (applied / dismissed / stale) — AID-379. */
  setProposalStatus: (
    messageId: string,
    status: FormPrefillProposalStatus
  ) => void;
  setMessageFeedback: (messageId: string, feedback: FeedbackType) => void;
}

export const useChatStore = create<ChatState>((set) => ({
  isOpen: false,
  messages: [],
  options: [],
  isLoading: false,
  isInitialising: false,
  sessionId: null,
  agentSessionId: null,
  sessionInitialized: false,
  thinkingSteps: [],
  currentThinkingStep: null,
  streamingMessageId: null,
  isStreaming: false,
  setIsOpen: (open) => set({ isOpen: open }),
  addMessage: (content, isUser, isSystem = false) =>
    set((state) => ({
      messages: [
        ...state.messages,
        {
          id: uuidv4(),
          content,
          timestamp: new Date(),
          isUser,
          isSystem,
        },
      ],
    })),
  removeSystemMessages: () =>
    set((state) => ({
      messages: state.messages.filter((message) => !message.isSystem),
    })),
  setOptions: (options) => set({ options }),
  clearOptions: () => set({ options: [] }),
  setIsLoading: (loading) => set({ isLoading: loading }),
  setIsInitialising: (initialising) => set({ isInitialising: initialising }),
  clearMessages: () =>
    set({
      messages: [],
      sessionId: null,
      agentSessionId: null,
      sessionInitialized: false,
    }),
  setSessionId: (sessionId) => set({ sessionId }),
  setAgentSessionId: (agentSessionId) => set({ agentSessionId }),
  setSessionInitialized: (initialized) =>
    set({ sessionInitialized: initialized }),
  initializeSession: () =>
    set({
      sessionId: null,
      agentSessionId: null,
      sessionInitialized: false,
      messages: [],
    }),
  addThinkingStep: (step) =>
    set((state) => ({
      thinkingSteps: [...state.thinkingSteps, step],
    })),
  setCurrentThinkingStep: (step) => set({ currentThinkingStep: step }),
  clearThinkingSteps: () =>
    set({ thinkingSteps: [], currentThinkingStep: null }),
  startStreamingMessage: (messageId) =>
    set((state) => {
      const newMessage: ChatMessage = {
        id: messageId,
        content: '',
        timestamp: new Date(),
        isUser: false,
        isSystem: false,
        metadata: {
          thinkingSteps: [],
          reasoningSteps: [],
          stateUpdates: [],
          toolCalls: [],
        },
      };

      return {
        streamingMessageId: messageId,
        isStreaming: true,
        messages: [...state.messages, newMessage],
      };
    }),
  appendToStreamingMessage: (content) =>
    set((state) => {
      if (!state.streamingMessageId) {
        return state;
      }

      const currentMessage = state.messages.find(
        (msg) => msg.id === state.streamingMessageId
      );
      const newContent = (currentMessage?.content || '') + content;

      // Always append exactly as received, preserving all whitespace
      return {
        messages: state.messages.map((msg) =>
          msg.id === state.streamingMessageId
            ? { ...msg, content: newContent }
            : msg
        ),
      };
    }),
  finishStreamingMessage: (finalContent?: string) =>
    set((state) => {
      if (finalContent && state.streamingMessageId) {
        // Replace the streaming message content with the properly formatted final content
        // IMPORTANT: Keep all existing message properties including metadata!
        return {
          streamingMessageId: null,
          isStreaming: false,
          messages: state.messages.map((msg) =>
            msg.id === state.streamingMessageId
              ? { ...msg, content: finalContent } // This preserves metadata automatically
              : msg
          ),
        };
      }

      return {
        streamingMessageId: null,
        isStreaming: false,
      };
    }),

  updateStreamingMetadata: (metadata) =>
    set((state) => {
      if (!state.streamingMessageId) {
        return state;
      }

      return {
        ...state,
        messages: state.messages.map((msg) =>
          msg.id === state.streamingMessageId
            ? {
                ...msg,
                metadata: {
                  ...msg.metadata,
                  ...metadata,
                },
              }
            : msg
        ),
      };
    }),

  addThinkingToMetadata: (agentName, thinking, timestamp) =>
    set((state) => {
      if (!state.streamingMessageId) {
        return state;
      }

      return {
        ...state,
        messages: state.messages.map((msg) =>
          msg.id === state.streamingMessageId
            ? {
                ...msg,
                metadata: {
                  ...msg.metadata,
                  thinkingSteps: [
                    ...(msg.metadata?.thinkingSteps || []),
                    {
                      timestamp,
                      agentName,
                      thinking,
                    },
                  ],
                },
              }
            : msg
        ),
      };
    }),

  addToolCallToMetadata: (toolName, startTime, toolArgs, toolUseId) =>
    set((state) => {
      if (!state.streamingMessageId) {
        return state;
      }

      return {
        ...state,
        messages: state.messages.map((msg) =>
          msg.id === state.streamingMessageId
            ? {
                ...msg,
                metadata: {
                  ...msg.metadata,
                  toolCalls: [
                    ...(msg.metadata?.toolCalls || []),
                    { toolName, startTime, toolArgs, toolUseId },
                  ],
                },
              }
            : msg
        ),
      };
    }),

  completeToolCallInMetadata: ({
    toolName,
    endTime,
    success,
    result,
    resultTruncated,
    toolUseId,
  }) =>
    set((state) => {
      if (!state.streamingMessageId) {
        return state;
      }

      return {
        messages: state.messages.map((msg) =>
          msg.id === state.streamingMessageId
            ? {
                ...msg,
                metadata: {
                  ...msg.metadata,
                  toolCalls: (msg.metadata?.toolCalls || []).map((tool) => {
                    // Prefer the stable toolUseId so parallel same-name calls
                    // never mis-correlate; fall back to name + first-open.
                    const matches = toolUseId
                      ? tool.toolUseId === toolUseId
                      : tool.toolName === toolName && !tool.endTime;

                    return matches
                      ? { ...tool, endTime, success, result, resultTruncated }
                      : tool;
                  }),
                },
              }
            : msg
        ),
      };
    }),

  pushAgentActivity: (event) =>
    set((state) => {
      if (!state.streamingMessageId) {
        return state;
      }

      return {
        messages: state.messages.map((msg) =>
          msg.id === state.streamingMessageId
            ? {
                ...msg,
                metadata: {
                  ...msg.metadata,
                  activity: [...(msg.metadata?.activity ?? []), event],
                },
              }
            : msg
        ),
      };
    }),

  setProposal: (messageId, proposal) =>
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === messageId
          ? {
              ...msg,
              metadata: {
                ...msg.metadata,
                proposal: { ...proposal, status: 'pending' },
              },
            }
          : msg
      ),
    })),

  setProposalStatus: (messageId, status) =>
    set((state) => ({
      messages: state.messages.map((msg) =>
        // Only advance an existing proposal — a status with no proposal to
        // describe would leave a half-built object the card would try to render.
        msg.id === messageId && msg.metadata?.proposal
          ? {
              ...msg,
              metadata: {
                ...msg.metadata,
                proposal: { ...msg.metadata.proposal, status },
              },
            }
          : msg
      ),
    })),

  setMessageFeedback: (messageId, feedback) =>
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === messageId ? { ...msg, feedbackGiven: feedback } : msg
      ),
    })),
}));
